<template>
  <div class="reply-box" :class="replyBoxClass">
    <banner
      v-if="showSelfAssignBanner"
      action-button-variant="clear"
      color-scheme="secondary"
      class="banner--self-assign"
      :banner-message="$t('CONVERSATION.NOT_ASSIGNED_TO_YOU')"
      :has-action-button="true"
      :action-button-label="$t('CONVERSATION.ASSIGN_TO_ME')"
      @click="onClickSelfAssign"
    />
    <reply-top-panel
      v-if="showTopPanel"
      :mode="replyType"
      :set-reply-mode="setReplyMode"
      :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
      :characters-remaining="charactersRemaining"
      :popout-reply-box="popoutReplyBox"
      :hide-reply-button="isAnIncomingCallsChannel"
      @click="$emit('click')"
    />
    <!--    d99d-->
    <div
      v-if="isReplyDisabled && isOnlineWaInboxV2"
      class="reply-box__top"
      style="padding: 2.6rem 0 3.5rem 1.6rem"
    >
      <woot-button
        v-if="hasInboxPermission('Inbox reply')"
        name="template-button"
        variant="clear"
        color-scheme="info"
        icon="brand-whatsapp"
        @click.prevent="showWhatsAppTemplatesModal = true"
      >
        {{ $t('WHATSAPP_TEMPLATES.MODAL.BUTTON_TO_OPEN_TITLE') }}
      </woot-button>
    </div>
    <div v-else class="reply-box__top">
      <reply-to-message
        v-if="shouldShowReplyToMessage"
        :message="inReplyTo"
        @dismiss="resetReplyToMessage"
      />
      <canned-response
        v-if="
          showMentions &&
          hasSlashCommand &&
          hasInboxPermission('Inbox canned responses', inbox.id)
        "
        v-on-clickaway="hideMentions"
        class="normal-editor__canned-box"
        :search-key="mentionSearchKey"
        @click="replaceMentionCommandInMessage"
      />
      <emoji-input
        v-if="showEmojiPicker"
        v-on-clickaway="hideEmojiPicker"
        :class="emojiDialogClassOnExpandedLayoutAndRTLView"
        :on-click="emojiOnClick"
      />
      <reply-email-head
        v-if="showReplyHead"
        :cc-emails.sync="ccEmails"
        :bcc-emails.sync="bccEmails"
        :fwd-emails.sync="fwdEmails"
        :from-email.sync="fromEmail"
        :to-emails.sync="toEmails"
        :fwd-enabled="isAnEmailApiChannel"
        :fwd-opened="true"
        :from-enabled="isAnEmailApiChannel"
      />
      <woot-audio-recorder
        v-if="showAudioRecorderEditor"
        ref="audioRecorderInput"
        :audio-record-format="audioRecordFormat"
        @state-recorder-progress-changed="onStateProgressRecorderChanged"
        @state-recorder-changed="onStateRecorderChanged"
        @finish-record="onFinishRecorder"
      />
      <resizable-text-area
        v-else-if="!showRichContentEditor"
        ref="messageInput"
        v-model="message"
        class="input"
        :placeholder="messagePlaceHolder"
        :disabled="!hasInboxPermission('Inbox reply')"
        :min-height="4"
        :signature="signatureToApply"
        :allow-signature="true"
        :send-with-signature="sendWithSignature"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
      />
      <email-woot-message-editor
        v-else-if="isAnEmailChannel"
        v-model="message"
        :editor-id="editorStateId"
        class="input"
        :is-private="isOnPrivateNote"
        :placeholder="messagePlaceHolder"
        :update-selection-with="updateEditorSelectionWith"
        :min-height="4"
        :enable-variables="true"
        :variables="messageVariables"
        :signature="signatureToApply"
        :allow-signature="true"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
        @toggle-user-mention="toggleUserMention"
        @toggle-canned-menu="toggleCannedMenu"
        @toggle-variables-menu="toggleVariablesMenu"
        @clear-selection="clearEditorSelection"
      />
      <woot-message-editor
        v-else
        v-model="message"
        :editor-id="editorStateId"
        class="input"
        :is-private="isOnPrivateNote"
        :placeholder="messagePlaceHolder"
        :disabled="!hasInboxPermission('Inbox reply')"
        :update-selection-with="updateEditorSelectionWith"
        :min-height="4"
        :enable-variables="true"
        :variables="messageVariables"
        :signature="signatureToApply"
        :allow-signature="true"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
        @toggle-user-mention="toggleUserMention"
        @toggle-canned-menu="toggleCannedMenu"
        @toggle-variables-menu="toggleVariablesMenu"
        @clear-selection="clearEditorSelection"
      />
    </div>
    <div v-if="hasAttachments" class="attachment-preview-box" @paste="onPaste">
      <attachment-preview
        class="flex-col mt-4"
        :attachments="attachedFiles"
        :remove-attachment="removeAttachment"
      />
    </div>
    <message-signature-missing-alert
      v-if="isSignatureEnabledForInbox && !isSignatureAvailable"
    />
    <reply-bottom-panel
      v-if="!isReplyDisabled"
      :last-message-time="lastMessageTime"
      :conversation-id="conversationId"
      :enable-multiple-file-upload="enableMultipleFileUpload"
      :has-whatsapp-templates="hasWhatsappTemplates"
      :inbox="inbox"
      :is-on-private-note="isOnPrivateNote"
      :is-recording-audio="isRecordingAudio"
      :is-send-disabled="isReplyButtonDisabled"
      :mode="replyType"
      :on-file-upload="file => fileUploadHandler(file, fileUploadOptions)"
      :on-send="onSendReply"
      :recording-audio-duration-text="recordingAudioDurationText"
      :recording-audio-state="recordingAudioState"
      :send-button-text="replyButtonLabel"
      :show-audio-recorder="showAudioRecorder"
      :show-editor-toggle="
        isAPIInbox && !isOnPrivateNote && !isSomeOfOnlineWaInbox /* d99d */
      "
      :show-emoji-picker="showEmojiPicker"
      :show-file-upload="showFileUpload"
      :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
      :toggle-audio-recorder="toggleAudioRecorder"
      :toggle-emoji-picker="toggleEmojiPicker"
      :file-kinds-accepted-to-upload="allowedAttachments"
      :message="message"
      :new-conversation-modal-active="newConversationModalActive"
      :close-reply-box="closeReplyBox"
      @selectWhatsappTemplate="openWhatsappTemplateModal"
      @toggle-editor="toggleRichContentEditor"
      @replace-text="replaceText"
      @insert-text="insertText"
      @set-delay="setDelay"
    />
    <online-wa-templates-modal
      v-if="isOnlineWaInboxV2"
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />
    <whatsapp-templates
      v-else
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import alertMixin from 'shared/mixins/alertMixin';

import CannedResponse from './CannedResponse.vue';
import ReplyToMessage from './ReplyToMessage.vue';
import ResizableTextArea from 'shared/components/ResizableTextArea.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import MessageSignatureMissingAlert from './MessageSignatureMissingAlert';
import Banner from 'dashboard/components/ui/Banner.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import EmailWootMessageEditor from 'dashboard/components/widgets/WootWriter/EmailEditor/Index.vue';
import WootAudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
  replaceVariablesInMessage,
} from '@chatwoot/utils';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import { buildHotKeys } from 'shared/helpers/KeyboardHelpers';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
// d99d
import OnlineWaTemplates from './watemplates/OnlineWaTemplateSelector';
import OnlineWaTemplatesModal from './watemplates/OnlineWaModal.vue';
// .
import { DirectUpload } from 'activestorage';
import { frontendURL } from '../../../helper/URLHelper';
import { trimContent, debounce } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import { isEditorHotKeyEnabled } from 'dashboard/mixins/uiSettings';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import rtlMixin from 'shared/mixins/rtlMixin';
import conversationMixin from 'dashboard/mixins/conversations';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  appendSignature,
  removeSignature,
  replaceSignature,
  extractTextFromMarkdown,
} from 'dashboard/helper/editorHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import DocalAPI from 'dashboard/api/docal';
import FormsAPI from 'dashboard/api/integrations/forms';

const EmojiInput = () => import('shared/components/emoji/EmojiInput');

// The `forms_` templates register their url button as `<forms host>/{{1}}`, so the variable has to
// carry the whole path and query of the generated link. Keeping only the last segment dropped the
// `/forms/<id>` prefix and produced links like `<forms host>/answer?t=...`.
const formLinkSuffix = formLink => {
  try {
    const { pathname, search } = new URL(formLink);
    return `${pathname.replace(/^\//, '')}${search}`;
  } catch (error) {
    return formLink;
  }
};

export default {
  components: {
    // d99d
    OnlineWaTemplates,
    EmailWootMessageEditor,
    OnlineWaTemplatesModal,
    // .
    EmojiInput,
    CannedResponse,
    ReplyToMessage,
    ResizableTextArea,
    AttachmentPreview,
    ReplyTopPanel,
    ReplyEmailHead,
    ReplyBottomPanel,
    WootMessageEditor,
    WootAudioRecorder,
    Banner,
    WhatsappTemplates,
    MessageSignatureMissingAlert,
  },
  mixins: [
    clickaway,
    inboxMixin,
    uiSettingsMixin,
    alertMixin,
    messageFormatterMixin,
    rtlMixin,
    fileUploadMixin,
    conversationMixin,
    hasPermissionMixin,
  ],
  props: {
    closeReplyBox: {
      type: Function,
      default: null,
    },
    popoutReplyBox: {
      type: Boolean,
      default: false,
    },
    showTopPanel: {
      type: Boolean,
      default: true,
    },
    isShowing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      message: '',
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      isUploading: false,
      replyType: REPLY_EDITOR_MODES.REPLY,
      mentionSearchKey: '',
      hasSlashCommand: false,
      bccEmails: '',
      fwdEmails: '', // d99d
      fromEmail: '', // d99d
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      delayHours: null,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      getMessageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      lastOutgoingEmail: 'getLastOutgoingMessageInSelectedChat',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    messageSignature() {
      const signature = this.getMessageSignature(this.inboxId)?.body || '';
      if (this.attachmentSignature) {
        return (
          signature +
          `\n ![This is signature](${this.attachmentSignature} "This is signature")`
        );
      }
      return signature;
    },
    attachmentSignature() {
      return this.getMessageSignature(this.inboxId)?.attachment_url;
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.MESSAGE_REPLY_TO
        )
      );
    },
    showRichContentEditor() {
      if (this.isOnPrivateNote || this.isRichEditorEnabled) {
        return true;
      }

      if (this.isAPIInbox) {
        const {
          display_rich_content_editor: displayRichContentEditor = false,
        } = this.uiSettings;
        return displayRichContentEditor;
      }

      return false;
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : 0;
        this.$store.dispatch('setCurrentChatAssignee', agent);
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            this.showAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    showSelfAssignBanner() {
      if (this.message !== '' && !this.isOnPrivateNote) {
        if (!this.assignedAgent) {
          return true;
        }
        if (this.assignedAgent.id !== this.currentUser.id) {
          return true;
        }
      }

      return false;
    },
    hasWhatsappTemplates() {
      return (
        !!this.$store.getters['inboxes/getWhatsAppTemplates'](this.inboxId)
          .length &&
        // || (this.isOnlineWaInboxV2) // d99d - first it was added then disabled
        !this.isOnPrivateNote
      ); // d99d
    },
    isPrivate() {
      if (this.isAnIncomingCallsChannel) return true; // d99d
      if (this.currentChat.can_reply || this.isAWhatsAppChannel) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      return this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (!this.hasInboxPermission('Inbox reply')) return true;
      if (this.isATwitterInbox) return true;
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isASmsInbox) {
        if (this.inbox?.message_max_length)
          return this.inbox.message_max_length; // d99d
        return MESSAGE_MAX_LENGTH.SMS;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      if (!this.hasInboxPermission('Inbox attach files')) return false;
      if (this.isA99digitalSmsInbox) return false;

      return (
        this.isAWebWidgetInbox ||
        this.isALineChannel ||
        this.isAFacebookInbox ||
        (this.isAFacebook2Inbox && !this.conversationType) || // d99d
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel
      );
    },
    allowedAttachments() {
      return this.isALineChannel
        ? 'image/*,video/*'
        : this.isAFacebook2Inbox
        ? 'image/*'
        : undefined; // d99d added isAFacebook2Inbox
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = isEditorHotKeyEnabled(this.uiSettings, 'cmd_enter')
        ? '(ctrl + ↵)'
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    hasRecordedAudio() {
      return (
        this.$refs.audioRecorderInput &&
        this.$refs.audioRecorderInput.hasAudio()
      );
    },
    isRichEditorEnabled() {
      return this.isAWebWidgetInbox || this.isAnEmailChannel;
    },
    showAudioRecorder() {
      return (
        !this.isOnPrivateNote &&
        this.showFileUpload &&
        !this.isALineChannel &&
        !this.isAFacebook2Inbox &&
        !this.isA99digitalSmsInbox &&
        !this.isAnEmailChannel
      ); // d99d two last
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    emojiDialogClassOnExpandedLayoutAndRTLView() {
      if (this.popoutReplyBox) {
        return 'emoji-dialog--expanded';
      }
      if (this.isRTLView) {
        return 'emoji-dialog--rtl';
      }
      return '';
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.signatureToApply;
    },
    sendWithSignature() {
      return !!this.messageSignature;
    },
    editorMessageKey() {
      const { editor_message_key: isEnabled } = this.uiSettings;
      return isEnabled;
    },
    commandPlusEnterToSendEnabled() {
      return this.editorMessageKey === 'cmd_enter';
    },
    enterToSendEnabled() {
      return this.editorMessageKey === 'enter';
    },
    conversationId() {
      return this.currentChat.id;
    },
    lastMessageTime() {
      return this.isOnlineWaInboxV2
        ? this.currentChat.last_incoming_message_time
        : null;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppChannel || this.isAPIInbox) {
        return AUDIO_FORMATS.OGG;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
      });
      return variables;
    },
    // d99d
    isReplyDisabled() {
      return (
        this.isOnlineWaInboxV2 &&
        !this.currentChat?.can_reply &&
        this.replyType === REPLY_EDITOR_MODES.REPLY
      );
    },
    // .
    // ensure that the signature is plain text depending on `showRichContentEditor`
    signatureToApply() {
      return this.showRichContentEditor
        ? this.messageSignature
        : extractTextFromMarkdown(this.messageSignature);
    },
    fileUploadOptions() {
      return {
        isMailboxFileUpload: this.isAnEmailChannel,
        isWhatsAppFileUpload: this.isAWhatsAppChannel,
      };
    },
  },
  watch: {
    currentChat(conversation, oldConversation) {
      if (conversation.id === oldConversation.id) return; // d99d

      const { can_reply: canReply } = conversation;

      if (this.isOnPrivateNote) {
        return;
      }

      if (this.isAnIncomingCallsChannel) {
        this.replyType = REPLY_EDITOR_MODES.NOTE; // d99d
      } else if (canReply || this.isAWhatsAppChannel) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.setCCAndToEmailsFromLastChat();
      this.fetchAndSetReplyTo();
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
      }
    },
    // d99d set mention search key when command start index is present
    message(updatedMessage) {
      this.hasSlashCommand =
        updatedMessage[this.mentionCommandSlashIndex] === '/' &&
        !this.showRichContentEditor;

      if (this.hasSlashCommand) {
        this.mentionSearchKey = updatedMessage
          .substring(this.mentionCommandStartIndex)
          .split(/[\s\n]/)[0];

        this.showMentions = true;
      }

      this.doAutoSaveDraft();
    },
    // .
    replyType(updatedReplyType, oldReplyType) {
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
    isShowing(newVal) {
      if (newVal) {
        this.setCCAndToEmailsFromLastChat();
      }
    },
  },

  mounted() {
    // d99d - incoming calls inboxes accept private notes only
    if (this.isAnIncomingCallsChannel) {
      this.replyType = REPLY_EDITOR_MODES.NOTE;
    }
    this.getFromDraft();
    // Don't use the keyboard listener mixin here as the events here are supposed to be
    // working even if input/textarea is focussed.
    this.$el.addEventListener('paste', this.onPaste);

    document.addEventListener('keydown', this.handleKeyEvents);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );

    this.fetchAndSetReplyTo();
    bus.$on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);
    bus.$on(BUS_EVENTS.TOGGLE_FORWARD_TO_MESSAGE, this.onToggleNote);
    if (this.closeReplyBox) {
      bus.$on(BUS_EVENTS.CLOSE_REPLY, this.closeReplyBox);
    }
    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    bus.$on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
  },
  destroyed() {
    document.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    bus.$off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);
    bus.$off(BUS_EVENTS.TOGGLE_FORWARD_TO_MESSAGE, this.onToggleNote);
    if (this.closeReplyBox) {
      bus.$off(BUS_EVENTS.CLOSE_REPLY, this.closeReplyBox);
    }
  },
  beforeDestroy() {
    bus.$off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
  },
  methods: {
    toggleRichContentEditor() {
      this.updateUISettings({
        display_rich_content_editor: !this.showRichContentEditor,
      });

      const plainTextSignature = extractTextFromMarkdown(this.messageSignature);

      if (!this.showRichContentEditor && this.messageSignature) {
        // remove the old signature -> extract text from markdown -> attach new signature
        let message = removeSignature(this.message, this.messageSignature);
        message = extractTextFromMarkdown(message);
        message = appendSignature(message, plainTextSignature);

        this.message = message;
      } else {
        this.message = replaceSignature(
          this.message,
          plainTextSignature,
          this.messageSignature
        );
      }
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = `draft-${conversationId}-${replyType}`;
        const draftToSave = trimContent(this.message || '');

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }

      return this.sendWithSignature
        ? appendSignature(message, this.signatureToApply)
        : removeSignature(message, this.signatureToApply);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    handleKeyEvents(e) {
      const keyCode = buildHotKeys(e);
      if (keyCode === 'escape') {
        this.hideEmojiPicker();
        this.hideMentions();
      } else if (keyCode === ' ') {
        this.hideMentions();
      } else if (keyCode === 'meta+k') {
        const ninja = document.querySelector('ninja-keys');
        ninja.open();
        e.preventDefault();
      } else if (keyCode === 'enter' && this.isAValidEvent('enter')) {
        this.onSendReply();
        e.preventDefault();
      } else if (
        ['meta+enter', 'ctrl+enter'].includes(keyCode) &&
        this.isAValidEvent('cmd_enter')
      ) {
        this.onSendReply();
      } else if (keyCode === 'shift+enter') {
        this.hideMentions();
      } else if (keyCode === '/') {
        this.setMentionCommandIndexes(e);
      } else {
        this.checkCursorLocation(e);
      }
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        isEditorHotKeyEnabled(this.uiSettings, selectedKey)
      );
    },
    // d99d hide mention when cursor moved before the command slash
    checkCursorLocation(e) {
      const cursorLocation = e.target.selectionStart;
      if (this.mentionCommandStartIndex > cursorLocation) {
        this.hideMentions();
      }
    },
    // .
    onPaste(e) {
      if (!this.hasInboxPermission('Inbox attach files')) {
        return;
      }
      const data = e.clipboardData.files;
      if (!this.showRichContentEditor && data.length !== 0) {
        this.$refs.messageInput.$el.blur();
      }
      if (!data.length || !data[0]) {
        return;
      }
      data.forEach(file => {
        const { name, type, size } = file;
        this.fileUploadHandler(
          { name, type, size, file: file },
          this.fileUploadOptions
        );
      });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    onClickSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return;
      }
      if (!this.showMentions) {
        const isOnWhatsApp =
          this.isATwilioWhatsAppChannel ||
          this.isAWhatsAppCloudChannel ||
          this.is360DialogWhatsAppChannel;
        if (isOnWhatsApp && !this.isPrivate) {
          this.sendMessageAsMultipleMessages(this.message);
        } else {
          const messagePayload = this.getMessagePayload(this.message);
          this.sendMessage(messagePayload);
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        this.clearMessage();
        this.hideEmojiPicker();
        this.$emit('update:popoutReplyBox', false);
      }
    },
    sendMessageAsMultipleMessages(message) {
      const messages = this.getMessagePayloadForWhatsapp(message);
      messages.forEach(messagePayload => {
        this.sendMessage(messagePayload);
      });
    },
    sendMessageAnalyticsData(isPrivate) {
      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? this.$track(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : this.$track(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    setDelay(delayHours) {
      this.delayHours = delayHours;
      this.onSendReply();
      this.delayHours = null;
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
    async sendMessage(messagePayload) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        bus.$emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        bus.$emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        this.showAlert(errorMessage);
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      if (messagePayload.doc) {
        try {
          await this.attachSigningLinkToPayload(messagePayload);
        } catch (error) {
          console.error(error);
          return;
        }
      }
      if (messagePayload.form) {
        try {
          await this.attachFormLinkToPayload(messagePayload);
        } catch (error) {
          console.error(error);
          this.showAlert(this.$t('FORMS_MODAL.LINK_ERROR'));
          return;
        }
      }
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async attachSigningLinkToPayload(messagePayload) {
      const conversationId = this.currentChat.id;
      const documentId = messagePayload.doc.document_id;
      const response = await DocalAPI.getSigningLink(
        this.accountId,
        documentId,
        conversationId
      );
      const signingLink = response.data?.signing_url

      const hash = signingLink?.split('/')?.pop();
      if (!messagePayload.contentAttributes) {
        messagePayload.contentAttributes = {};
      }
      if (!messagePayload.contentAttributes.template_info) {
        messagePayload.contentAttributes.template_info = {};
      }
      messagePayload.contentAttributes.template_info.websiteVariable = hash;
      delete messagePayload.doc;
    },
    async attachFormLinkToPayload(messagePayload) {
      const response = await FormsAPI.getFormLink(
        messagePayload.form.publicId,
        this.currentChat.id
      );
      const formLink = response.data?.form_link;
      if (!formLink) throw new Error('Empty form link');

      if (!messagePayload.contentAttributes) {
        messagePayload.contentAttributes = {};
      }
      if (!messagePayload.contentAttributes.template_info) {
        messagePayload.contentAttributes.template_info = {};
      }
      messagePayload.contentAttributes.template_info.websiteVariable =
        formLinkSuffix(formLink);
      delete messagePayload.form;
    },
    replaceText(message) {
      const updatedMessage = replaceVariablesInMessage({
        message,
        variables: this.messageVariables,
      });
      setTimeout(() => {
        this.$track(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
        this.message = updatedMessage;
      }, 100);
    },
    insertText(text) {
      const currentMsg = this.message || '';
      const separator = currentMsg.trim() ? ' ' : '';
      this.message = currentMsg + separator + text;
    },
    // d99d replace slash command in resizable text area
    replaceMentionCommandInMessage(message) {
      const textFragmentForCommand = replaceVariablesInMessage({
        message,
        variables: this.messageVariables,
      });

      const messageBeforeCommand = this.message.slice(
        0,
        this.mentionCommandSlashIndex
      );

      const commandEndIndex =
        this.mentionCommandStartIndex + this.mentionSearchKey.length;
      const messageAfterCommand = this.message.slice(commandEndIndex);

      const updatedMessage =
        messageBeforeCommand + textFragmentForCommand + messageAfterCommand;

      this.hideMentions();
      setTimeout(() => {
        this.$track(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
        this.message = updatedMessage;
      }, 100);
    },
    // .
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      // d99d - incoming calls inboxes accept private notes only
      if (this.isAnIncomingCallsChannel) mode = REPLY_EDITOR_MODES.NOTE;
      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      if (
        canReply ||
        this.isAWhatsAppChannel ||
        this.isAnIncomingCallsChannel
      ) {
        this.replyType = mode;
      }
      if (this.showRichContentEditor) {
        if (this.isRecordingAudio) {
          this.toggleAudioRecorder();
        }
        return;
      }
      this.$nextTick(() => this.$refs.messageInput?.focus());
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    insertEmoji(emoji, selectionStart, selectionEnd) {
      const { message } = this;
      const newMessage =
        message.slice(0, selectionStart) +
        emoji +
        message.slice(selectionEnd, message.length);
      this.message = newMessage;
    },
    emojiOnClick(emoji) {
      if (this.showRichContentEditor) {
        this.updateEditorSelectionWith = emoji;
        this.onFocus();
      }
      if (!this.showRichContentEditor) {
        const { selectionStart, selectionEnd } = this.$refs.messageInput.$el;
        this.insertEmoji(emoji, selectionStart, selectionEnd);
      }
    },
    clearMessage() {
      this.message = '';
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        this.message = appendSignature(this.message, this.signatureToApply);
      }
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.fwdEmails = ''; // d99d
      this.fromEmail = ''; // d99d
      this.toEmails = '';
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      this.isRecorderAudioStopped = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.clearMessage();
        this.clearEmailField();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (this.isRecordingAudio) {
        if (!this.isRecorderAudioStopped) {
          this.isRecorderAudioStopped = true;
          this.$refs.audioRecorderInput.stopAudioRecording();
        } else if (this.isRecorderAudioStopped) {
          this.$refs.audioRecorderInput.playPause();
        }
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    // d99d reset clear mention command state
    hideMentions() {
      this.mentionCommandStartIndex = null;
      this.mentionCommandSlashIndex = null;
      this.showMentions = false;
      this.mentionSearchKey = null;
    },
    //
    // d99d set mention command start and slash indexes
    setMentionCommandIndexes(e) {
      this.mentionCommandSlashIndex = e.target.selectionStart;
      this.mentionCommandStartIndex = this.mentionCommandSlashIndex + 1;
    },
    // .
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
    },
    onStateProgressRecorderChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onStateRecorderChanged(state) {
      this.recordingAudioState = state;
      if (state && 'notallowederror'.includes(state)) {
        this.toggleAudioRecorder();
      }
    },
    onFinishRecorder(file) {
      return file && this.fileUploadHandler(file, this.fileUploadOptions);
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    attachFile({ blob, file }) {
      if (!this.enableMultipleFileUpload) this.attachedFiles = []; // d99d

      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
        });
      };
    },
    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
    },
    getMessagePayloadForWhatsapp(message) {
      const multipleMessagePayload = [];

      if (this.attachedFiles && this.attachedFiles.length) {
        let caption = message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile = this.globalConfig.directUploadsEnabled
            ? attachment.blobSignedId
            : attachment.resource.file;
          const attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: caption,
            sender: this.sender,
          };
          multipleMessagePayload.push(attachmentPayload);
          caption = '';
        });
      } else {
        const messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        };
        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      const messagePayload = {
        conversationId: this.currentChat.id,
        message,
        private: this.isPrivate,
        sender: this.sender,
        delay_hours: this.delayHours,
      };

      if (this.inReplyTo?.id) {
        messagePayload.contentAttributes = {
          in_reply_to: this.inReplyTo.id,
        };
      }
      // d99d
      if (this.isAPIInbox && this.messageSignature) {
        messagePayload.contentAttributes = {
          ...messagePayload.contentAttributes,
          message_signature: this.messageSignature,
        };
      }
      // .
      // if (
      //   this.attachmentSignature &&
      //   this.isAnEmailChannel &&
      //   this.isSignatureEnabledForInbox
      // ) {
      //   messagePayload.files = [this.attachmentSignature];
      // }
      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = messagePayload.files || [];
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }

      // d99d
      if (!this.isOnPrivateNote) {
        // better to rifine that block
        if (this.fwdEmails) {
          messagePayload.toEmails = this.fwdEmails;
          messagePayload.forwarded = true;
        }
        if (this.fromEmail) {
          messagePayload.fromEmail = this.fromEmail;
        }
      }
      // .

      return messagePayload;
    },
    setCCAndToEmailsFromLastChat() {
      // d99d
      if (!this.isAnEmailChannel) return;
      if (this.isAnEmailApiChannel) {
        this.fromEmail = this.lastOutgoingEmail?.content_attributes?.from_email;
      }
      // .

      if (!this.lastEmail) return;

      const {
        content_attributes: { email: emailAttributes = {} },
      } = this.lastEmail;

      // Retrieve the email of the current conversation's sender
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      let cc = emailAttributes.cc ? [...emailAttributes.cc] : [];
      let to = [];

      // there might be a situation where the current conversation will include a message from a third person,
      // and the current conversation contact is in CC.
      // This is an edge-case, reported here: CW-1511 [ONLY FOR INTERNAL REFERENCE]
      // So we remove the current conversation contact's email from the CC list if present
      if (cc.includes(conversationContact)) {
        cc = cc.filter(email => email !== conversationContact);
      }

      // If the last incoming message sender is different from the conversation contact, add them to the "to"
      // and add the conversation contact to the CC
      if (!emailAttributes.from.includes(conversationContact)) {
        to.push(...emailAttributes.from);
        cc.push(conversationContact);
      }

      // Remove the conversation contact's email from the BCC list if present
      let bcc = (emailAttributes.bcc || []).filter(
        email => email !== conversationContact
      );

      // Ensure only unique email addresses are in the CC list
      bcc = [...new Set(bcc)];
      cc = [...new Set(cc)];
      to = [...new Set(to)];

      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
      this.toEmails = to.join(', ');
      this.fwdEmails = ''; // d99d
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );
      const mode = LocalStorage.getFromJsonStore(replyStorageKey, 'mode');
      if (mode) {
        this.setReplyMode(mode);
      }
      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (message.id === replyToMessageId) {
          return true;
        }
        return false;
      });
    },
    onToggleNote() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_FORWARD_TO;

      const mode = LocalStorage.getFromJsonStore(replyStorageKey, 'mode');
      if (mode) {
        this.setReplyMode(mode);
      }
    },

    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      bus.$emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
  },
};
</script>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.banner--self-assign {
  @apply py-2;
}

.attachment-preview-box {
  @apply bg-transparent py-0 px-4;
}

.reply-box {
  @apply border-t border-slate-50 dark:border-slate-700 bg-white dark:bg-slate-900;

  &.is-private {
    @apply bg-yellow-50 dark:bg-yellow-200;
  }
}

// d99d
.reply-notif {
  background: var(--w-75);
  color: var(--s-500);
  padding: var(--space-half) var(--space-normal);
}
// .

.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative py-0 px-4 -mt-px border-t border-solid border-slate-50 dark:border-slate-700;
}

.emoji-dialog {
  @apply top-[unset] -bottom-10 left-16 right-[unset];
  direction: ltr;

  // d99d
  .dashboard--blank & {
    left: 0;
  }
  // .

  &::before {
    transform: rotate(90deg);
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
    @apply right-80 bottom-2 rtl:right-0 rtl:left-80;
  }
}
// d99d
.template-select {
  padding: var(--space-normal) var(--space-normal) 0 var(--space-normal);
  min-height: 80px;
}
// .

.emoji-dialog--rtl {
  @apply left-[unset] right-16;

  // d99d
  .dashboard--blank & {
    right: 0;
  }
  // .
  &::before {
    transform: rotate(270deg);
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));

    // d99d
    .dashboard--blank & {
      left: unset !important;
      right: var(--space-normal);
    }
    // .
  }
}

.dashboard--blank .emoji-dialog, // d99d
.emoji-dialog--expanded {
  @apply left-[unset] bottom-0 absolute z-[100];

  &::before {
    transform: rotate(0deg);
    @apply left-1 -bottom-2;
  }
}

.normal-editor__canned-box {
  width: calc(100% - 2 * var(--space-normal));
  left: var(--space-normal);
}
</style>
